/*
 * rheincool — Header-Verhalten (HANDOFF.md §5.1)
 * - Sticky-Kompaktmodus: ab 80 px Scroll bekommt <html> die Klasse
 *   .rc-scrolled → USP-Leiste klappt ein, Logo wird kleiner (CSS).
 * - Mobile-Drawer: öffnen/schließen mit Fokusfalle und Escape.
 * - Mega-Menü (details/summary): schließt bei Klick außerhalb und Escape.
 */
class RcHeader extends HTMLElement {
  connectedCallback() {
    this.drawer = this.querySelector('#MenuDrawer');
    this.openButton = this.querySelector('[data-drawer-open]');
    this.lastFocused = null;

    if (this.classList.contains('rc-header--sticky')) {
      this.initSticky();
    }
    this.initDrawer();
    this.initMenus();
  }

  /* --- Sticky --- */
  initSticky() {
    let ticking = false;
    // Hysterese: Einklappen erst ab 160 px, Ausklappen erst unter 24 px.
    // Der Abstand muss größer sein als die Höhenänderung des Headers,
    // sonst pendelt die Klasse an der Schwelle und der Header zittert.
    const enterAt = 160;
    const exitAt = 24;
    const html = document.documentElement;

    /*
     * Der Header ist sticky, steht aber im Fluss: Wird er beim Scrollen
     * kürzer, schrumpft das Dokument und der gesamte Inhalt darunter rutscht
     * um dieselbe Strecke nach oben — auf dem Handy waren das 183 px. Für den
     * Nutzer fühlt sich das an, als hätte er zu weit gescrollt.
     *
     * Deshalb wird die Klasse nie direkt gesetzt, sondern immer hierüber: Die
     * Dokumenthöhe wird vor und nach dem Umschalten gemessen und die
     * Scrollposition im selben Frame um die Differenz nachgeführt. Der Inhalt
     * bleibt dadurch exakt stehen; sichtbar ist nur, dass der Header kürzer
     * wird. Das Auslesen von scrollHeight erzwingt das Neuberechnen des
     * Layouts noch vor dem Zeichnen — sonst käme der Ausgleich ein Bild zu
     * spät und man sähe genau den Sprung, den er verhindern soll.
     */
    let hoehendifferenz = 0;

    /*
     * Jede Layoutänderung des Headers wird hier gekapselt: Dokumenthöhe vor und
     * nach der Änderung messen, Scrollposition um die Differenz nachführen. Das
     * Auslesen von scrollHeight erzwingt das Neuberechnen des Layouts noch vor
     * dem Zeichnen — sonst käme der Ausgleich ein Bild zu spät.
     */
    const mitAusgleich = (aendern) => {
      const vorher = html.scrollHeight;
      aendern();
      const differenz = vorher - html.scrollHeight;
      if (differenz !== 0) {
        hoehendifferenz = Math.max(hoehendifferenz, Math.abs(differenz));
        window.scrollTo(0, Math.max(0, window.scrollY - differenz));
      }
    };

    /*
     * Die beiden mobilen Chip-Reihen gehen in zwei Schritten:
     *   1. .rc-scrolled blendet sie weich aus (nur Deckkraft und Verschiebung,
     *      kein Layout — der Inhalt bleibt dabei stehen).
     *   2. Nach der Blende kommen sie per hidden aus dem Fluss; erst dieser
     *      Schritt ändert die Höhe und wird ausgeglichen.
     * Die Höhe selbst zu animieren ginge nicht ohne Ruckeln: Sie steckt im
     * Fluss, jede Zwischenstufe verschöbe den Seiteninhalt mit.
     */
    const reihen = this.querySelector('.rc-header__mobile-rows');
    const ruhig = window.matchMedia('(prefers-reduced-motion: reduce)');
    let lauf = null;

    /*
     * Die Chip-Reihen stecken im klebenden Header — ihr Verschwinden ist
     * zwangsläufig eine Höhenänderung, und die verschiebt alles darunter.
     * In einem Bild ausgeführt wirkt das abgehackt.
     *
     * Deshalb wird die Höhe über mehrere Bilder gefahren und die
     * Scrollposition in jedem Bild um genau die Differenz nachgeführt: Der
     * Inhalt steht still, während der Header gleitet.
     *
     * Die Zielhöhe wird EINMAL beim Start gemerkt, nicht bei jedem Öffnen neu
     * per height:auto gemessen — eine solche Messung schreibt zwischendurch ins
     * Layout und verfälschte den Ausgleich der jeweils nächsten Bilder.
     */
    let offeneHoehe = reihen ? reihen.getBoundingClientRect().height : 0;

    const fahre = (nach) => {
      if (!reihen || !offeneHoehe) return;
      if (lauf) cancelAnimationFrame(lauf);

      const von = reihen.getBoundingClientRect().height;
      const ziel = nach === 'auf' ? offeneHoehe : 0;
      if (Math.abs(von - ziel) < 1) return;

      reihen.hidden = false;
      if (ruhig.matches) {
        mitAusgleich(() => { reihen.style.height = ziel + 'px'; });
        if (nach !== 'auf') reihen.hidden = true;
        return;
      }

      reihen.style.overflow = 'hidden';
      const t0 = performance.now();
      const dauer = 220;
      let letzte = Math.round(von);

      /*
       * Der Ausgleich rechnet hier NICHT über eine Messung der Dokumenthöhe,
       * sondern aus der Höhe, die wir selbst setzen. Grund: scrollHeight ist
       * ganzzahlig — bei gebrochenen Zwischenhöhen fällt die Messung mal auf-,
       * mal abgerundet aus, und der Rest von einem Pixel wandert als Zittern
       * in die Scrollposition. Auf ganze Pixel gerundet und mit der exakten
       * Differenz ausgeglichen bleibt nichts übrig, das zittern könnte.
       */
      const schritt = (jetzt) => {
        const p = Math.min(1, (jetzt - t0) / dauer);
        const e = p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2;
        const h = Math.round(von + (ziel - von) * e);
        const weg = letzte - h;
        reihen.style.height = h + 'px';
        if (weg !== 0 && window.scrollY > 0) window.scrollBy(0, -weg);
        letzte = h;
        if (p < 1) { lauf = requestAnimationFrame(schritt); return; }
        lauf = null;
        reihen.style.overflow = '';
        if (nach === 'auf') reihen.style.height = '';
        else reihen.hidden = true;
      };
      lauf = requestAnimationFrame(schritt);
    };

    const setzeKompakt = (kompakt) => {
      if (html.classList.contains('rc-scrolled') === kompakt) return;
      mitAusgleich(() => html.classList.toggle('rc-scrolled', kompakt));
      fahre(kompakt ? 'zu' : 'auf');
    };

    const update = () => {
      const y = window.scrollY;
      /*
       * Der Ausgleich setzt die Scrollposition beim Einklappen um die
       * Höhendifferenz zurück. Läge die Einklappschwelle darunter, fiele man
       * dabei sofort unter die Ausklappschwelle und der Header würde pendeln.
       * Die Schwelle wächst deshalb mit der gemessenen Differenz mit — sonst
       * bräche das Verhalten in dem Moment, in dem der Händler eine vierte
       * Marke anlegt und die Markenreihe umbricht.
       */
      /* Feste Schwelle. Die frühere mitwachsende Variante (Höhendifferenz +
         Puffer) konnte sich über die Ausgleichsschritte hochschaukeln und das
         Einklappen ganz verhindern. Der Ausgleich haelt die Scrollposition
         ohnehin, es braucht keine dynamische Schwelle mehr. */
      const schwelle = enterAt;
      if (y > schwelle) {
        /* Sofort einklappen, nicht erst nach Scroll-Ruhe. Verzoegert wirkte es
           nachtraeglich — der Header fuhr ein, wenn die Bewegung schon vorbei
           war. Der Preis ist ein kurzes Stocken auf iOS: Der Ausgleich schreibt
           in die Scrollposition, und Safari kappt dabei den Schwung. Das ist
           die bewusst gewaehlte Seite des Kompromisses. */
        setzeKompakt(true);
      } else if (y < exitAt) {
        setzeKompakt(false);
      }
      ticking = false;
    };
    window.addEventListener(
      'scroll',
      () => {
        if (!ticking) {
          ticking = true;
          requestAnimationFrame(update);
        }
      },
      { passive: true }
    );
    update();
  }

  /* --- Mobile-Drawer --- */
  initDrawer() {
    if (!this.drawer || !this.openButton) return;

    this.openButton.addEventListener('click', () => this.openDrawer());
    this.drawer.querySelectorAll('[data-drawer-close]').forEach((el) => {
      el.addEventListener('click', () => this.closeDrawer());
    });
    this.drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
      }
      if (event.key === 'Tab') this.trapFocus(event);
    });
  }

  openDrawer() {
    this.lastFocused = document.activeElement;
    this.drawer.hidden = false;
    // Reflow abwarten, damit die Einblend-Transition greift
    requestAnimationFrame(() => this.drawer.classList.add('is-open'));
    this.openButton.setAttribute('aria-expanded', 'true');
    document.body.classList.add('rc-no-scroll');
    const firstFocusable = this.drawer.querySelector('button, a, input');
    if (firstFocusable) firstFocusable.focus();
  }

  closeDrawer() {
    this.drawer.classList.remove('is-open');
    this.openButton.setAttribute('aria-expanded', 'false');
    document.body.classList.remove('rc-no-scroll');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const hide = () => {
      this.drawer.hidden = true;
    };
    if (reducedMotion) {
      hide();
    } else {
      setTimeout(hide, 250);
    }
    if (this.lastFocused) this.lastFocused.focus();
  }

  trapFocus(event) {
    const focusables = this.drawer.querySelectorAll(
      'button:not([disabled]), a[href], input:not([disabled]), summary, [tabindex]:not([tabindex="-1"])'
    );
    if (!focusables.length) return;
    const first = focusables[0];
    const last = focusables[focusables.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  /* --- Mega-Menü --- */
  initMenus() {
    const menus = this.querySelectorAll('header-menu > details');
    if (!menus.length) return;

    document.addEventListener('click', (event) => {
      menus.forEach((details) => {
        if (details.open && !details.contains(event.target)) details.open = false;
      });
    });
    this.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      menus.forEach((details) => {
        if (!details.open) return;
        details.open = false;
        details.querySelector('summary').focus();
      });
    });
    // Nur ein Panel gleichzeitig offen
    menus.forEach((details) => {
      details.addEventListener('toggle', () => {
        if (!details.open) return;
        menus.forEach((other) => {
          if (other !== details) other.open = false;
        });
      });
    });
  }
}

customElements.define('rc-header', RcHeader);

/*
 * Verfügbarkeits-Indikator im Beratungs-Block (Header-Handoff v2 §3):
 * grüner Punkt + „Beratung erreichbar" innerhalb der Öffnungszeiten,
 * außerhalb grauer Punkt + „Wir rufen morgen zurück".
 */
(() => {
  'use strict';
  const el = document.querySelector('[data-header-availability]');
  if (!el) return;
  const map = {};
  (el.dataset.hours || '').split(',').forEach((part) => {
    const m = part.trim().match(/^(\d)(?:-(\d))?:(\d{1,2})-(\d{1,2})$/);
    if (!m) return;
    const to = m[2] ? parseInt(m[2], 10) : parseInt(m[1], 10);
    for (let d = parseInt(m[1], 10); d <= to; d += 1) map[d] = [parseInt(m[3], 10), parseInt(m[4], 10)];
  });
  const now = new Date();
  const win = map[now.getDay() === 0 ? 7 : now.getDay()];
  const h = now.getHours() + now.getMinutes() / 60;
  const open = Boolean(win && h >= win[0] && h < win[1]);
  const dot = el.querySelector('[data-availability-dot]');
  const text = el.querySelector('[data-availability-text]');
  if (dot) dot.hidden = false;
  // Statt pauschal "morgen": Den naechsten Zeitpunkt aus demselben Fahrplan
  // ableiten, aus dem sich auch der Offen-Status ergibt. Am Freitagabend ist
  // "morgen" schlicht falsch — dann muss dort Montag stehen.
  function naechsterTermin() {
    const heute = now.getDay() === 0 ? 7 : now.getDay();
    for (let i = 0; i <= 7; i += 1) {
      const tag = ((heute - 1 + i) % 7) + 1;
      const fenster = map[tag];
      if (!fenster) continue;
      if (i === 0 && h >= fenster[0]) continue;   // heute schon vorbei
      const stunde = String(fenster[0]);
      if (i === 0) return (el.dataset.nextToday || '').replace('{stunde}', stunde);
      if (i === 1) return (el.dataset.nextTomorrow || '').replace('{stunde}', stunde);
      const namen = (el.dataset.weekdays || '').split(',');
      const name = namen[tag - 1];
      if (!name) return '';
      return (el.dataset.nextWeekday || '').replace('{tag}', name).replace('{stunde}', stunde);
    }
    return '';
  }

  const termin = naechsterTermin();
  // Ohne belegbaren Zeitpunkt die neutrale Zusatzzeile statt eines
  // abgeschnittenen "wieder erreichbar ..." — kein Hinweis ohne Deckung.
  const zusatz = open
    ? (el.dataset.onlineNote || '')
    : (termin
      ? (el.dataset.offlineNote || '').replace('{zeit}', termin)
      : (el.dataset.offlinePlain || ''));

  if (text) text.textContent = open ? el.dataset.onlineText : el.dataset.offlineText;
  const note = el.querySelector('[data-availability-note]');
  if (note) {
    note.textContent = zusatz;
    note.hidden = zusatz === '';
  }
  el.classList.toggle('is-open', open);
  el.dataset.rcTrackVerfuegbar = open ? 'true' : 'false';
})();
